/**
 * Effective parameters, as the inspector presents them.
 *
 * Requirement 4.2 and 13.2: an instance stores only what differs from its
 * template, and reads back the union. The inspector shows the union and marks
 * which values are the designer's own. This mirrors
 * `core/library/effective.py`; the server remains the authority, and anything
 * this module lets through is validated again on check.
 *
 * Edges are the asymmetric case: their hydraulic geometry is stored whole
 * (invariant 4 — physics reads it without the template), so "overridden" for a
 * channel's width means "differs from the template default", not "is stored".
 */
import type {
  DesignDocument,
  EdgeDocument,
  Finding,
  NodeDocument,
  ParameterDeclaration,
  TemplateDocument,
} from "../design/document";
import { HYDRAULIC_KEYS } from "../editor/reducer";
import { formatQuantity } from "./units";

export interface ParameterRow {
  key: string;
  declaration: ParameterDeclaration;
  /** Canonical SI, or the enum/bool/str value. Null when nothing resolves. */
  effective: unknown;
  /** The template's default, for "default 300 µm" and for clearing. */
  defaultValue: unknown;
  /** True when this instance's value is the designer's rather than the template's. */
  overridden: boolean;
}

// Matches _RELATIVE_TOLERANCE in effective.py: a round trip through a unit
// conversion must not manufacture an override.
const RELATIVE_TOLERANCE = 1e-12;

export function sameValue(a: unknown, b: unknown): boolean {
  if (typeof a === "number" && typeof b === "number") {
    if (a === b) return true;
    return Math.abs(a - b) <= RELATIVE_TOLERANCE * Math.max(Math.abs(a), Math.abs(b));
  }
  return a === b;
}

function isHydraulic(key: string): boolean {
  return (HYDRAULIC_KEYS as readonly string[]).includes(key);
}

/** The stored value for a key, or undefined when the element does not store one. */
function storedValue(element: NodeDocument | EdgeDocument, key: string): unknown {
  if ("from_port" in element) {
    if (isHydraulic(key)) return (element.hydraulic as Record<string, unknown> | undefined)?.[key];
    return element.properties?.[key];
  }
  return element.parameters?.[key];
}

/** Every declared parameter with its effective value, in declaration order. */
export function effectiveParameters(
  element: NodeDocument | EdgeDocument,
  template: TemplateDocument | undefined,
): ParameterRow[] {
  return (template?.parameters ?? []).map((declaration) => {
    const stored = storedValue(element, declaration.key);
    const defaultValue = declaration.default ?? null;
    const present = stored !== undefined && stored !== null;
    return {
      key: declaration.key,
      declaration,
      effective: present ? stored : defaultValue,
      defaultValue,
      overridden: present && !sameValue(stored, defaultValue),
    };
  });
}

export function isNumeric(declaration: ParameterDeclaration): boolean {
  return declaration.value_type === "float" || declaration.value_type === "int";
}

/** "seat_width" -> "seat width" */
export function parameterLabel(key: string, declaration?: ParameterDeclaration): string {
  return declaration?.label || key.replace(/_/g, " ");
}

// Matches _BOUND_TOLERANCE in validation.py: typing exactly the stated minimum
// in the unit one thinks in must not land a few ulps below it.
const BOUND_TOLERANCE = 1e-12;

function atBound(value: number, bound: number): boolean {
  return Math.abs(value - bound) <= BOUND_TOLERANCE * Math.max(Math.abs(value), Math.abs(bound));
}

/**
 * Why a value cannot be stored for this parameter, as a finding against the
 * field (requirement 13.4), or null when it can. Codes match the server's so a
 * finding reads the same whichever side produced it.
 */
export function validateParameter(ref: string, declaration: ParameterDeclaration, value: unknown): Finding | null {
  const key = declaration.key;
  const finding = (code: string, message: string, detail: Record<string, unknown> = {}): Finding => ({
    code,
    message,
    severity: "error",
    path: ref,
    detail: { key, ...detail },
  });
  if (isNumeric(declaration)) {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return finding("invalid_value", `${parameterLabel(key, declaration)} must be a number`);
    }
    if (declaration.value_type === "int" && !Number.isInteger(value)) {
      return finding("invalid_value", `${parameterLabel(key, declaration)} must be a whole number`);
    }
    const unit = declaration.unit ?? "dimensionless";
    const { minimum, maximum } = declaration;
    if (minimum !== null && minimum !== undefined && value < minimum && !atBound(value, minimum)) {
      return finding(
        "out_of_bounds",
        `${parameterLabel(key, declaration)} is ${formatQuantity(value, unit)}, below the minimum ${formatQuantity(minimum, unit)}`,
        { minimum, actual: value },
      );
    }
    if (maximum !== null && maximum !== undefined && value > maximum && !atBound(value, maximum)) {
      return finding(
        "out_of_bounds",
        `${parameterLabel(key, declaration)} is ${formatQuantity(value, unit)}, above the maximum ${formatQuantity(maximum, unit)}`,
        { maximum, actual: value },
      );
    }
    return null;
  }
  if (declaration.value_type === "bool" && typeof value !== "boolean") {
    return finding("invalid_value", `${parameterLabel(key, declaration)} must be true or false`);
  }
  if (declaration.value_type === "enum" && !(declaration.enum_values ?? []).includes(String(value))) {
    return finding(
      "invalid_value",
      `${parameterLabel(key, declaration)} must be one of ${(declaration.enum_values ?? []).join(", ")}`,
      { permitted: declaration.enum_values ?? [] },
    );
  }
  if (declaration.value_type === "str" && typeof value !== "string") {
    return finding("invalid_value", `${parameterLabel(key, declaration)} must be text`);
  }
  return null;
}

/** "150 – 1000 µm", or null when unbounded. Shown under numeric fields. */
export function describeBounds(declaration: ParameterDeclaration): string | null {
  const unit = declaration.unit ?? "dimensionless";
  const { minimum, maximum } = declaration;
  const has = (bound: number | null | undefined): bound is number => bound !== null && bound !== undefined;
  if (has(minimum) && has(maximum)) return `${formatQuantity(minimum, unit)} – ${formatQuantity(maximum, unit)}`;
  if (has(minimum)) return `at least ${formatQuantity(minimum, unit)}`;
  if (has(maximum)) return `at most ${formatQuantity(maximum, unit)}`;
  return null;
}

/** The parameter a finding is about, if it names one. Check findings say `parameter`; validation says `key`. */
export function findingParameter(finding: Finding): string | null {
  const detail = finding.detail ?? {};
  const named = detail.parameter ?? detail.key;
  return typeof named === "string" ? named : null;
}

/** The element a finding is about: the first segment of its path. */
export function findingElement(finding: Finding): string | null {
  return finding.path ? finding.path.split(".")[0]! : null;
}

/** Findings that belong against one field of one element. */
export function fieldFindings(findings: readonly Finding[], ref: string, key: string): Finding[] {
  return findings.filter((finding) => findingElement(finding) === ref && findingParameter(finding) === key);
}

const SCHEMA_PATH = /^(nodes|edges|group_instances|treatment_applications|reagent_deposits|thermal_zones|text_notes)\.(\d+)(?:\.(.*))?$/;

/**
 * A save refusal locates problems by schema path ("nodes.3.parameters.width"),
 * which means nothing to a designer and cannot be navigated to. Restate it by
 * element identifier, and name the parameter where the path reaches one, so the
 * finding lands in the panel and against its field like any other.
 */
export function locateSchemaFinding(document: DesignDocument, finding: Finding): Finding {
  const match = finding.path ? SCHEMA_PATH.exec(finding.path) : null;
  if (!match) return finding;
  const collection = document[match[1] as keyof DesignDocument] as { local_id?: string }[] | undefined;
  const element = collection?.[Number(match[2])];
  if (!element?.local_id) return finding;
  const rest = match[3] ?? "";
  const parameter = /^(?:parameters|properties|hydraulic)\.([^.]+)/.exec(rest)?.[1];
  return {
    ...finding,
    path: rest ? `${element.local_id}.${rest}` : element.local_id,
    detail: parameter ? { ...(finding.detail ?? {}), key: parameter } : finding.detail,
  };
}
